import { useState } from "react";
import type {
  InboundGoods,
  ShortageInput,
} from "@shared-core/data/supplier-portal-adapter";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  ACTION_WORDING,
  FORM_WORDING,
  count,
  dayMonth,
  inboundStatus,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { FormField } from "../../app/components/patterns/FormField";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { StaffViewReadOnly } from "../../app/components/domain/StaffViewReadOnly";

/**
 * A service order's inbound goods (goods follow the service, brief decision 9): what is arriving from whom and
 * when, and a way to report a shortage on arrival.
 */
export function ArrivingSection({
  arriving,
  canReportShortage,
  onReportShortage,
}: {
  arriving: InboundGoods[];
  canReportShortage: boolean;
  onReportShortage: (input: ShortageInput) => Promise<void>;
}) {
  const [isReporting, setIsReporting] = useState(false);
  const [isReported, setIsReported] = useState(false);
  return (
    <div className="grid gap-3">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>From</TableHead>
              <TableHead>What</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead>Expected</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {arriving.map((goods) => (
              <TableRow key={goods.inbound_id}>
                <TableCell>{goods.from_supplier}</TableCell>
                <TableCell className="whitespace-normal">
                  {goods.description}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {count(goods.quantity)}
                </TableCell>
                <TableCell>
                  {goods.expected_on ? dayMonth(goods.expected_on) : "—"}
                </TableCell>
                <TableCell>
                  <StatusPill {...inboundStatus(goods)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {isReported && (
        <p role="status" className="text-sm font-semibold text-green-700">
          ✓ {FORM_WORDING.shortageSent}
        </p>
      )}
      {canReportShortage &&
        (isReporting ? (
          <ShortageForm
            delivered={arriving.filter((g) => g.status === "delivered")}
            onCancel={() => setIsReporting(false)}
            onSubmit={async (input) => {
              await onReportShortage(input);
              setIsReporting(false);
              setIsReported(true);
            }}
          />
        ) : (
          <StaffViewReadOnly>
            <div>
              <Button
                variant="outline"
                onClick={() => {
                  setIsReported(false);
                  setIsReporting(true);
                }}
              >
                {ACTION_WORDING.reportShortage}
              </Button>
            </div>
          </StaffViewReadOnly>
        ))}
    </div>
  );
}

function ShortageForm({
  delivered,
  onSubmit,
  onCancel,
}: {
  delivered: InboundGoods[];
  onSubmit: (input: ShortageInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [inboundId, setInboundId] = useState(delivered[0]?.inbound_id ?? "");
  const [received, setReceived] = useState("");
  const [note, setNote] = useState("");
  const write = useFieldErrorWrite("We couldn't send your report to Pollards.");
  return (
    <form
      noValidate
      aria-label={FORM_WORDING.shortageTitle}
      className="grid gap-3 rounded-lg border bg-card p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void write.run(() =>
          onSubmit({
            inbound_id: inboundId,
            quantity_received: received === "" ? Number.NaN : Number(received),
            note,
          }),
        );
      }}
    >
      <p className="text-sm font-semibold">{FORM_WORDING.shortageTitle}</p>
      {write.banner && (
        <p role="alert" className="text-sm text-red-600">
          {write.banner}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          id="shortage-what"
          label={FORM_WORDING.shortageWhat}
          error={write.fieldErrors.inbound_id}
        >
          {(control) => (
            <Select value={inboundId} onValueChange={setInboundId}>
              <SelectTrigger {...control} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {delivered.map((g) => (
                  <SelectItem key={g.inbound_id} value={g.inbound_id}>
                    {g.description} ({g.from_supplier}, {count(g.quantity)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
        <FormField
          id="shortage-received"
          label={FORM_WORDING.shortageReceived}
          error={write.fieldErrors.quantity_received}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              inputMode="numeric"
              min={0}
              value={received}
              onChange={(event) => setReceived(event.target.value)}
            />
          )}
        </FormField>
      </div>
      <FormField
        id="shortage-note"
        label={FORM_WORDING.shortageNote}
        error={write.fieldErrors.note}
      >
        {(control) => (
          <Input
            {...control}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        )}
      </FormField>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {ACTION_WORDING.cancel}
        </Button>
        <Button type="submit" disabled={write.isBusy}>
          {ACTION_WORDING.sendToPollards}
        </Button>
      </div>
    </form>
  );
}
